import React, { useState, useEffect } from 'react';
import { CheckoutConfig, PlanDetail } from '../../../types';
import HostedCheckout from '../../HostedCheckout';
import { INITIAL_PLANS } from '../../../services/mockData';
import { api } from '../../../services/api';
import { CheckoutDeviceFrame } from './CheckoutDeviceFrame';
import { CheckoutPreviewFrame } from './CheckoutPreviewFrame';

interface CheckoutStudioPreviewProps {
  config: CheckoutConfig;
  previewVariation: { testId: string; varId: string } | null;
  onClearVariationPreview: () => void;
  selectedPlan?: PlanDetail;
  onChangePlan?: (plan: PlanDetail) => void;
}

export const CheckoutStudioPreview: React.FC<CheckoutStudioPreviewProps> = ({
  config,
  previewVariation,
  onClearVariationPreview,
  selectedPlan = INITIAL_PLANS[2],
  onChangePlan,
}) => {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>(selectedPlan.currency || 'USD');
  const [prefilledData, setPrefilledData] = useState<Record<string, string> | undefined>(undefined);
  const [availablePlans, setAvailablePlans] = useState<PlanDetail[]>(INITIAL_PLANS);

  useEffect(() => {
    let isMounted = true;
    api.plans.getAll().then((dbPlans) => {
      if (isMounted && Array.isArray(dbPlans) && dbPlans.length > 0) {
        setAvailablePlans(dbPlans as PlanDetail[]);
      }
    }).catch(() => {});
    return () => { isMounted = false; };
  }, []);

  const activeVariation = previewVariation
    ? config.abTests?.find((t) => t.id === previewVariation.testId)?.variations?.find((v) => v.id === previewVariation.varId)
    : null;

  const effectiveConfig: CheckoutConfig = activeVariation ? { ...config, ...(activeVariation.config || {}) } : config;
  const simulatedPlan: PlanDetail = { ...selectedPlan, currency };

  const handlePrefillTest = () => {
    setPrefilledData(prefilledData ? undefined : {
      name: 'Alex Morgan',
      email: 'alex.morgan@acmecorp.io',
      phone: '+1 (555) 234-5678',
      country: 'US',
      company: 'Acme Technologies Inc.',
      tax_id: 'US-99382109',
    });
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className={`flex flex-col h-full bg-slate-900/90 text-white ${isFullscreen ? 'fixed inset-0 z-50 p-4' : 'p-3'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-1 bg-slate-800/90 p-1 rounded-lg border border-slate-700">
          {(['desktop', 'tablet', 'mobile'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDevice(d)}
              className={`px-2.5 py-1 rounded text-xs font-bold capitalize transition-all ${
                device === d ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {onChangePlan && (
            <select
              value={selectedPlan.id}
              onChange={(e) => {
                const p = availablePlans.find((x) => x.id === e.target.value);
                if (p) onChangePlan(p);
              }}
              className="bg-slate-800 border border-slate-700 text-xs font-bold text-slate-200 px-2 py-1 rounded-lg outline-none"
            >
              {availablePlans.map((p) => (
                <option key={p.id} value={p.id}>{p.tier || (p as any).name} (${p.price})</option>
              ))}
            </select>
          )}

          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-xs font-bold text-slate-200 px-2 py-1 rounded-lg outline-none"
          >
            {['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD', 'JPY'].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={handlePrefillTest}
            className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-colors ${
              prefilledData ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-slate-800 text-slate-300 border-slate-700'
            }`}
          >
            {prefilledData ? 'Buyer Filled' : 'Autofill Buyer'}
          </button>

          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            title="Refresh Preview"
            className="p-1 bg-slate-800 text-slate-300 rounded border border-slate-700 hover:text-white"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
          </button>

          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title="Fullscreen"
            className="p-1 bg-slate-800 text-slate-300 rounded border border-slate-700 hover:text-white"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect width="18" height="18" x="3" y="3" rx="2" /></svg>
          </button>
        </div>
      </div>

      {previewVariation && activeVariation && (
        <div className="flex items-center justify-between px-3 py-1 my-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded text-emerald-300 text-xs font-bold shrink-0">
          <span>A/B Variation Preview: {activeVariation.name}</span>
          <button type="button" onClick={onClearVariationPreview} className="text-[11px] underline">Exit</button>
        </div>
      )}

      <div className="flex-1 min-h-0 flex items-center justify-center p-2 overflow-auto">
        <CheckoutDeviceFrame
          device={device}
          currency={currency}
          planTier={simulatedPlan.tier}
        >
          <CheckoutPreviewFrame
            device={device}
            refreshKey={refreshKey}
          >
            <HostedCheckout
              plan={simulatedPlan}
              onClose={() => {}}
              configOverride={effectiveConfig}
              initialFormDataOverride={prefilledData}
            />
          </CheckoutPreviewFrame>
        </CheckoutDeviceFrame>
      </div>
    </div>
  );
};

